import React from 'react';
import PropTypes from 'prop-types';
import { Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { usePublicUiLocale } from '../../contexts/PublicUiLocaleContext';
import { useAdminPanelTheme } from '../../project-modules/store/AdminPanelThemeContext';

function formatUsd(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (v < 0) return `-$${Math.abs(v).toFixed(2)}`;
  return `$${v.toFixed(2)}`;
}

export default function SuperAdminBalanceChip({ summary, loading, onClick }) {
  const { sa } = usePublicUiLocale();
  const { ui } = useAdminPanelTheme();

  if (loading && !summary) {
    return <CircularProgress size={18} sx={{ mr: 0.5 }} />;
  }

  if (!summary) return null;

  const balance = Number(summary.balance_usd);
  const debt = Number(summary.debt_usd) || 0;
  const hasIssue =
    debt > 0 ||
    (summary.projects || []).some(
      (p) => p.billing_suspended || p.insufficient_for_renewal,
    );

  const label = formatUsd(balance);
  const tooltip = debt > 0
    ? `${sa.billingBalance}: ${label} · ${sa.billingDebt}: ${formatUsd(debt)}`
    : `${sa.billingBalance}: ${label}`;

  const chip = (
    <Chip
      size="small"
      icon={<AccountBalanceWalletIcon sx={{ fontSize: '16px !important' }} />}
      label={label}
      onClick={onClick}
      clickable={Boolean(onClick)}
      color={hasIssue ? 'warning' : 'default'}
      variant="outlined"
      sx={{
        height: 28,
        borderColor: hasIssue ? undefined : ui.border,
        '& .MuiChip-label': { fontWeight: 600, fontSize: 13, px: 0.75 },
      }}
    />
  );

  if (onClick) {
    return (
      <Tooltip title={tooltip}>
        {chip}
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip}>
      <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: hasIssue ? 'warning.main' : ui.textPrimary }}>
        {label}
      </Typography>
    </Tooltip>
  );
}

SuperAdminBalanceChip.propTypes = {
  summary: PropTypes.object,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
};

SuperAdminBalanceChip.defaultProps = {
  summary: null,
  loading: false,
  onClick: undefined,
};
